import type { Theme } from '@mui/material';
import type { IAccordionData } from '@rumblefish/ui/src/components/Rumblefish23Theme/Accordion/CustomizedAccordion.types';
import highPerformanceApiServicesIcon from '../../../public/svg/icons/rawPerformance.svg';
import codeAuditsAndOptimizationIcon from '../../../public/svg/icons/rwdServices/codeAuditsAndOptimalization.svg';
import fullStackWebApplicationsIcon from '../../../public/svg/icons/rwdServices/fullStackApplications.svg';
import legacySystemMigrationIcon from '../../../public/svg/icons/rwdServices/legacySystemMigration.svg';
import realTimeSystemsIcon from '../../../public/svg/icons/rwdServices/realTimeSystems.svg';
import webAssemblyApplicationsIcon from '../../../public/svg/icons/rwdServices/webAssemblyApplications.svg';

export const getRustServicesAccordionData = (
  theme: Theme
): IAccordionData[] => [
  {
    title: 'Full-Stack Web Applications',
    customDescription: () => (
      <>
        We build complete web applications using modern Rust frameworks like
        Axum, Actix-web, and Rocket. From database integration to frontend
        rendering, we handle the entire stack. Whether you need server-side
        rendering, REST APIs, or GraphQL services, we've got you covered.
        <p>
          <b>What we deliver:</b>
        </p>
        <ul>
          <li>Modern web frameworks (Axum, Actix, Rocket, Warp)</li>
          <li>Type-safe database access with SQLx and Diesel</li>
          <li>Authentication and authorization systems</li>
          <li>WebSocket and real-time communication</li>
          <li>Server-side rendering with Tera and Askama</li>
          <li>Comprehensive API documentation</li>
        </ul>
      </>
    ),
    iconSvg: fullStackWebApplicationsIcon,
    gradientOnFocus: theme.mixins.orangeGradient?.background as string,
  },
  {
    title: 'High-Performance API Services',
    customDescription: () => (
      <>
        Build REST or GraphQL APIs that handle thousands of requests per second
        with minimal latency. Perfect for microservices architectures, mobile
        backends, and third-party integrations. We optimize for throughput,
        latency, and cost-effectiveness.
        <p>
          <b>Ideal for:</b>
        </p>
        <ul>
          <li>Microservices architectures</li>
          <li>Mobile app backends</li>
          <li>Payment processing systems</li>
          <li>Data aggregation services</li>
          <li>Real-time analytics APIs</li>
        </ul>
      </>
    ),
    iconSvg: highPerformanceApiServicesIcon,
    gradientOnFocus: theme.mixins.orangeGradient?.background as string,
  },
  {
    title: 'WebAssembly Applications',
    customDescription: () => (
      <>
        Run Rust code directly in the browser with WebAssembly. We've built
        everything from computational tools to games that leverage WASM's
        near-native performance. Perfect when JavaScript performance hits a
        wall.
        <p>
          <b>Use cases:</b>
        </p>
        <ul>
          <li>Image and video processing</li>
          <li>Data visualization and charting</li>
          <li>Cryptographic operations</li>
          <li>Game engines and simulations</li>
          <li>Scientific computing tools</li>
        </ul>
      </>
    ),
    iconSvg: webAssemblyApplicationsIcon,
    gradientOnFocus: theme.mixins.orangeGradient?.background as string,
  },
  {
    title: 'Legacy System Migration',
    customDescription: () => (
      <>
        Gradually replace performance bottlenecks in your existing systems with
        Rust microservices. We've helped companies migrate from Node.js, Python,
        and Go, typically seeing 5-10x performance improvements in critical
        paths.
        <p>
          <b>Our approach:</b>
        </p>
        <ul>
          <li>Identify performance bottlenecks</li>
          <li>Design interoperable architecture</li>
          <li>Gradual, risk-managed migration</li>
          <li>Comprehensive testing and validation</li>
          <li>Team training and knowledge transfer</li>
        </ul>
      </>
    ),
    iconSvg: legacySystemMigrationIcon,
    gradientOnFocus: theme.mixins.orangeGradient?.background as string,
  },
  {
    title: 'Real-Time Systems',
    customDescription: () => (
      <>
        Build systems that handle real-time data streams, WebSocket connections,
        and event-driven architectures. Rust's async ecosystem (Tokio,
        async-std) makes it perfect for applications that need to process
        thousands of concurrent connections.
        <p>
          <b>Perfect for:</b>
        </p>
        <ul>
          <li>Chat and messaging platforms</li>
          <li>Live dashboards and monitoring</li>
          <li>Trading and financial systems</li>
          <li>IoT data processing</li>
          <li>Multiplayer game servers</li>
        </ul>
      </>
    ),
    iconSvg: realTimeSystemsIcon,
    gradientOnFocus: theme.mixins.orangeGradient?.background as string,
  },
  {
    title: 'Code Audits and Optimization',
    customDescription: () => (
      <>
        Already have a Rust codebase? We review your code for performance
        bottlenecks, security issues, and architectural improvements. Our audits
        include actionable recommendations and implementation support.
      </>
    ),
    iconSvg: codeAuditsAndOptimizationIcon,
    gradientOnFocus: theme.mixins.orangeGradient?.background as string,
  },
];
